import {observer} from "mobx-react";
import * as React from "react";
import {withAppStores} from "../../../../AppStores";
import type {TransportLayer} from "../../../../data/TransportLayer";
import type {Catalog} from "../../../../data/models/Catalog";
import type {Xyicon} from "../../../../data/models/Xyicon";
import type {AppState} from "../../../../data/state/AppState";
import {Permission, XyiconFeature, FieldDataType} from "../../../../generated/api/base";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {FieldV5} from "../../details/FieldV5";
import {ClickToEditInputV5} from "../../input/clicktoedit/ClickToEditInputV5";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import type {Type} from "../../../../data/models/Type";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import type {Xyicon3D} from "../../../modules/space/spaceeditor/logic3d/elements3d/Xyicon3D";

interface ICatalogDefaultFieldsProps {
	readonly item: Catalog;
	readonly transport?: TransportLayer;
	readonly appState?: AppState;
}

interface ICatalogDefaultFieldsState {
	isUpdating: boolean;
	model: string;
	defaultModelName: string;
}

@withAppStores("transport", "appState")
@observer
export class CatalogDefaultFieldsV5 extends React.Component<ICatalogDefaultFieldsProps, ICatalogDefaultFieldsState> {
	constructor(props: ICatalogDefaultFieldsProps) {
		super(props);
		this.state = {
			isUpdating: false,
			model: props.item.model,
			defaultModelName: props.item.model,
		};
	}

	private isNameValid = (value: string) => {
		return this.props.appState.actions.isModelValidForCatalog(value, this.props.item);
	};

	private getErrorMessage = () => {
		const model = StringUtils.removeWhiteSpaces(this.state.model);

		return !model ? "Name cannot be empty!" : this.isNameValid(model) ? "" : "Name needs to be unique!";
	};

	private onModelChange = (value: string) => {
		this.setState({
			model: value,
		});
	};

	private onDoneChanging = async () => {
		const {item, appState} = this.props;
		const {model: modelState, defaultModelName} = this.state;
		const model = StringUtils.removeWhiteSpaces(modelState);
		const hasError = this.getErrorMessage();

		if (model === defaultModelName) {
			return;
		}

		if (hasError) {
			this.setState({
				model: item.model,
			});
			return;
		}

		item.setModel(model);

		this.setState({isUpdating: true});
		await TimeUtils.waitUpdate(appState.actions.updateFields([item], {model}), appState.app.notificationContainer);
		this.setState({isUpdating: false});
	};

	private onTypeChange = async (type: Type) => {
		const {item, appState, transport} = this.props;
		const xyicons = appState.actions.getList<Xyicon>(XyiconFeature.Xyicon).filter((xyicon) => xyicon.model === item.model);

		const title = "Confirm Type Change";
		const message = `Once you change the catalog type, fields (and data) not assigned to the new catalog type will be removed. There are ${xyicons.length} xyicons that will be updated with the new catalog type and assigned fields.  Do you wish to continue?`;
		const config = {
			ok: "Change",
			cancel: "Cancel",
		};

		const confirmed = await ConfirmWindowV5.open(message, title, config);

		if (confirmed) {
			item.typeId = type.id;
			const xyicon3Ds = xyicons.map((x) => appState.app.spaceViewRenderer.xyiconManager.getItemById(x.id) as Xyicon3D).filter((x3D) => !!x3D);

			await appState.app.spaceViewRenderer.spaceItemController.updateFilterState(undefined, false);
			await appState.actions.updateSpaceEditorCaptionsForAffectedAndLinkedItems(XyiconFeature.Xyicon, xyicon3Ds);
			await transport.updateCatalogType(item);
		}
	};

	public static getDerivedStateFromProps(props: ICatalogDefaultFieldsProps, state: ICatalogDefaultFieldsState) {
		const model = props.item.model;

		if (model !== state.defaultModelName) {
			return {
				model,
				defaultModelName: model,
			};
		}

		return null;
	}

	private get permission() {
		return this.props.appState.user.getOrganizationPermission(XyiconFeature.XyiconCatalog);
	}

	public override render() {
		const {item, appState} = this.props;
		const {isUpdating, model} = this.state;
		const hasPermission = this.permission >= Permission.Update;

		return (
			<>
				<FieldV5 label={appState.actions.getRefIdName(item.ownFeature)}>{item.refId}</FieldV5>
				<FieldV5
					className="TypeSelector"
					label="Type"
				>
					{hasPermission ? (
						<SelectInputV5
							options={appState.types[XyiconFeature.Xyicon]}
							renderOptionWhenClosed={(item) => item.name}
							selected={appState.actions.getTypeById(item.typeId)}
							onChange={this.onTypeChange}
							isSameWidth={true}
						/>
					) : (
						appState.actions.getTypeById(item.typeId).name
					)}
				</FieldV5>
				<FieldV5
					label="Model"
					className="stucked"
				>
					{hasPermission ? (
						<ClickToEditInputV5
							value={model}
							onLiveChange={this.onModelChange}
							dataType={FieldDataType.SingleLineText}
							isUpdating={isUpdating}
							onBlur={this.onDoneChanging}
							getErrorMessage={this.getErrorMessage}
						/>
					) : (
						item.model
					)}
				</FieldV5>
			</>
		);
	}
}
